import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { useState } from "react";
import { createMatchResolutionRequest } from "../shared";
import { BlacklistEntityType, CreateBlacklistMatchResolutionRequestDto } from "../types";

type CreateMatchResolutionRequestModalProps = {
  open: boolean;
  onClose: () => void;
  entityId: number;
  entityType: BlacklistEntityType;
  defaultLookupCode?: string;
  entityLabel?: string;
  onActionComplete?: () => Promise<void>;
};

const getEntityTypeLabel = (entityType: BlacklistEntityType): string => {
  switch (entityType) {
    case BlacklistEntityType.Person:
      return "Individual";
    case BlacklistEntityType.Company:
      return "Entity";
    default:
      return "Blacklist Record";
  }
};

const CreateMatchResolutionRequestModal = ({
  open,
  onClose,
  entityId,
  entityType,
  defaultLookupCode = "",
  entityLabel,
  onActionComplete
}: CreateMatchResolutionRequestModalProps) => {
  const [lookupCode, setLookupCode] = useState(defaultLookupCode);
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [lookupCodeError, setLookupCodeError] = useState<string | null>(null);
  const [reasonError, setReasonError] = useState<string | null>(null);

  const handleSubmit = async () => {
    const trimmedLookupCode = lookupCode.trim();
    const trimmedReason = reason.trim();
    let hasError = false;

    setSubmitError(null);
    setLookupCodeError(null);
    setReasonError(null);

    if (!trimmedLookupCode) {
      setLookupCodeError("Please enter the datawarehouse lookup code.");
      hasError = true;
    }

    if (!trimmedReason) {
      setReasonError("Please provide a reason for this resolution request.");
      hasError = true;
    }

    if (hasError) {
      return;
    }

    setIsSubmitting(true);
    try {
      const request: CreateBlacklistMatchResolutionRequestDto = {
        lookupCode: trimmedLookupCode,
        entityType,
        entityId,
        reason: trimmedReason
      };

      await createMatchResolutionRequest(request);

      if (onActionComplete) {
        await onActionComplete();
      }

      onClose();
    } catch (error) {
      console.error("Error creating match resolution request:", error);
      setSubmitError("An error occurred while submitting your request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Resolve a Contact
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {submitError && <Alert severity="error">{submitError}</Alert>}

          <Alert severity="info">
            This does not immediately suppress a blacklist match. It creates a request that must be approved before the
            report ignores this lookup-code and blacklist-record pair.
          </Alert>

          <Typography variant="body2" color="text.secondary">
            {getEntityTypeLabel(entityType)} ID: {entityId}
            {entityLabel ? ` - ${entityLabel}` : ""}
          </Typography>

          <TextField
            label="Contact/Company Lookup Code"
            value={lookupCode}
            onChange={(e) => setLookupCode(e.target.value)}
            fullWidth
            required
            error={Boolean(lookupCodeError)}
            helperText={lookupCodeError ?? "Use the lookup code from the report/datawarehouse row, not necessarily the blacklist row."}
          />

          <TextField
            label="Reason"
            multiline
            rows={4}
            fullWidth
            required
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            error={Boolean(reasonError)}
            helperText={reasonError ?? undefined}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? "Submitting..." : "Submit for Approval"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateMatchResolutionRequestModal;
